import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Paper,
  Typography,
  InputBase,
  ClickAwayListener,
  CircularProgress,
} from '@material-ui/core';
import { Search, ExpandMore, Close } from '@material-ui/icons';
import { useFormContext, useController } from 'react-hook-form';

import {
  Checkmark,
  DEBOUNCE_MS,
  IS_BLANK,
  IS_NOT_BLANK,
  SCROLL_OFFSET,
  UNUSED_FIELD_NAME,
} from '../../../constants/selectFilter.js';
import { MF_BLANK, MF_NOT_BLANK } from '../../../constants/selectFilter.js';
import RMSSelectFilterFooter from '../RMSSelectFilter/RMSSelectFilterFooter.jsx';
import { useLazyOptions } from './hooks/useLazyOptions.js';

const RMSSelectFilterWithLazyLoading = ({
  name,
  label,
  fetchFn,
  selected: selectedProp = [],
  onChange: onChangeProp,
  valueKey = 'value',
  labelKey = 'label',
  disabled = false,
  initialLabels,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const formContext = useFormContext();
  const control = formContext?.control;
  const isFormControlled = Boolean(name && control);

  const { field } = useController({
    name: isFormControlled ? name : UNUSED_FIELD_NAME,
    control,
    defaultValue: selectedProp,
    disabled: !isFormControlled,
  });

  const selected = isFormControlled ? field.value || [] : selectedProp;
  const onChange = isFormControlled ? field.onChange : onChangeProp;

  const debounceTimer = useRef(null);
  const listRef = useRef(null);

  const { options, loading, hasMore, onOpen, onSearch, onScrollBottom, onClose } = useLazyOptions(
    fetchFn
  );

  const isSentinel = (v) => v === MF_BLANK || v === MF_NOT_BLANK;

  const labelCacheRef = useRef(initialLabels || {});

  const labelLookup = useMemo(() => {
    const map = {};
    options.forEach((o) => {
      if (typeof o === 'object' && o !== null) {
        map[o[valueKey]] = o[labelKey] || o[valueKey];
      }
    });
    Object.assign(labelCacheRef.current, map);
    return labelCacheRef.current;
  }, [options, valueKey, labelKey]);

  const getLabel = useCallback(
    (val) => {
      if (val === MF_BLANK) return IS_BLANK;
      if (val === MF_NOT_BLANK) return IS_NOT_BLANK;
      return labelLookup[val] || val;
    },
    [labelLookup]
  );

  const optionValues = useMemo(
    () => options.map((o) => (typeof o === 'object' && o !== null ? o[valueKey] : o)),
    [options, valueKey]
  );

  const visible = useMemo(() => {
    const nonSentinel = optionValues.filter((o) => !isSentinel(o));
    if (!query && selected.length > 0) {
      const sel = nonSentinel.filter((o) => selected.includes(o));
      const unsel = nonSentinel.filter((o) => !selected.includes(o));
      return [...sel, ...unsel];
    }
    return nonSentinel;
  }, [optionValues, query, selected]);

  const nonSentinelSelected = selected.filter((v) => !isSentinel(v)).length;
  const nonSentinelTotal = optionValues.filter((o) => !isSentinel(o)).length;

  const toggle = useCallback(
    (val) => {
      if (val === MF_BLANK) {
        const without = selected.filter((v) => v !== MF_NOT_BLANK && v !== MF_BLANK);
        onChange(selected.includes(MF_BLANK) ? without : [...without, MF_BLANK]);
      } else if (val === MF_NOT_BLANK) {
        const without = selected.filter((v) => v !== MF_BLANK && v !== MF_NOT_BLANK);
        onChange(selected.includes(MF_NOT_BLANK) ? without : [...without, MF_NOT_BLANK]);
      } else {
        onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
      }
    },
    [selected, onChange]
  );

  const handleOpen = () => {
    if (disabled) return;
    const next = !open;
    setOpen(next);
    setQuery('');
    if (next) {
      onOpen();
    } else {
      onClose();
    }
  };

  const handleClickAway = () => {
    if (!open) return;
    setOpen(false);
    onClose();
  };

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => onSearch(val.trim()), DEBOUNCE_MS);
  };

  const clearQuery = () => {
    setQuery('');
    clearTimeout(debounceTimer.current);
    onSearch('');
  };

  const handleScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distFromBottom < SCROLL_OFFSET && hasMore && !loading) {
        onScrollBottom();
      }
    },
    [hasMore, loading, onScrollBottom]
  );

  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  const isActive = selected.length > 0;
  const btnLabel =
    selected.length === 0
      ? `All ${label}`
      : selected.length === 1
      ? getLabel(selected[0])
      : `${selected.length} selected`;

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div className="multi-filter-wrapper">
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            className={[
              'multi-filter-trigger-btn',
              isActive ? 'active' : '',
              disabled ? 'disabled' : '',
            ].join(' ')}
            onClick={handleOpen}
            onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
            aria-expanded={open}
            aria-haspopup="listbox">
            <Typography
              component="span"
              className={['multi-filter-trigger-label', isActive ? 'active' : ''].join(' ')}>
              {btnLabel}
            </Typography>
            {isActive && <span className="multi-filter-badge">{selected.length}</span>}
            <ExpandMore className={['multi-filter-chevron', open ? 'open' : ''].join(' ')} />
          </div>

          {open && (
            <Paper className="multi-filter-dropdown" elevation={0}>
              <div className="multi-filter-search-wrap">
                <Search className="multi-filter-search-icon" />
                <InputBase
                  className="multi-filter-search-input"
                  placeholder={`Search ${label.toLowerCase()}…`}
                  value={query}
                  onChange={handleQueryChange}
                  autoFocus
                  inputProps={{ 'aria-label': `Search ${label}` }}
                />
                {query ? (
                  <Close className="multi-filter-clear-search" onClick={clearQuery} />
                ) : (
                  loading && <CircularProgress size={14} thickness={5} style={{ marginRight: 6 }} />
                )}
              </div>

              <div
                ref={listRef}
                className="multi-filter-options-list"
                role="listbox"
                aria-multiselectable="true"
                onScroll={handleScroll}>
                {visible.length === 0 && !loading ? (
                  <div className="multi-filter-no-results">
                    {query ? `No matches for "${query}"` : 'No options available'}
                  </div>
                ) : (
                  <>
                    {visible.map((opt) => {
                      const checked = selected.includes(opt);
                      return (
                        <div
                          key={opt}
                          role="option"
                          aria-selected={checked}
                          className={['multi-filter-option', checked ? 'checked' : ''].join(' ')}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            toggle(opt);
                          }}>
                          <div
                            className={['multi-filter-checkbox', checked ? 'checked' : ''].join(
                              ' '
                            )}>
                            {checked && <Checkmark />}
                          </div>
                          <span
                            className={['multi-filter-option-label', checked ? 'checked' : ''].join(
                              ' '
                            )}>
                            {getLabel(opt)}
                          </span>
                        </div>
                      );
                    })}

                    {loading && (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                        <CircularProgress size={18} thickness={4} />
                      </div>
                    )}

                    {!hasMore && visible.length > 0 && (
                      <div
                        className="multi-filter-no-results"
                        style={{ color: '#999', fontSize: 11 }}>
                        All results loaded
                      </div>
                    )}
                  </>
                )}
              </div>

              <RMSSelectFilterFooter
                selected={selected}
                toggle={toggle}
                getLabel={getLabel}
                query={query}
                visible={visible}
                nonSentinelTotal={nonSentinelTotal}
                nonSentinelSelected={nonSentinelSelected}
                isActive={isActive}
                onChange={onChange}
              />
            </Paper>
          )}
        </div>
      </ClickAwayListener>
    </>
  );
};

export default RMSSelectFilterWithLazyLoading;
